// Polish-style number and date formatting. Decimal places adapt to magnitude
// so small fractional KPIs (xG/strzał) still read at a glance.

const PL = 'pl-PL'

export function fmtNumber(v: number | null | undefined, fractionDigits?: number): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  const f = fractionDigits ?? autoFractionDigits(v)
  return new Intl.NumberFormat(PL, {
    minimumFractionDigits: f,
    maximumFractionDigits: f,
  }).format(v)
}

export function autoFractionDigits(v: number): number {
  const a = Math.abs(v)
  if (Number.isInteger(v) && a >= 1) return 0
  if (a >= 100) return 0
  if (a >= 10) return 1
  if (a >= 1) return 2
  if (a >= 0.1) return 2
  return 3
}

export function fmtSigned(v: number | null | undefined, fractionDigits?: number): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  const s = fmtNumber(v, fractionDigits)
  if (v > 0) return `+${s}`
  return s
}

export function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(PL, { day: '2-digit', month: 'long', year: 'numeric' }).format(d)
}

export function fmtDateShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(PL, { day: '2-digit', month: '2-digit', year: '2-digit' }).format(d)
}

export function resultLabel(r: 'W' | 'D' | 'L'): string {
  return r === 'W' ? 'Zwycięstwo' : r === 'D' ? 'Remis' : 'Porażka'
}

/**
 * Polska odmiana rzeczownika przez liczebnik: 1 szansa / 2–4 szanse / 5+ szans.
 * Forma „few" wraca dla końcówek 2–4 POZA nastoletnimi (12–14 → „szans").
 * Bez tego liczniki w UI wychodzą jak „3 sytuacji".
 */
export function plCount(n: number, one: string, few: string, many: string): string {
  const d = Math.abs(n) % 10
  const dd = Math.abs(n) % 100
  if (Math.abs(n) === 1) return one
  return d >= 2 && d <= 4 && (dd < 12 || dd > 14) ? few : many
}

/** „0 szans" / „1 szansa" / „3 szanse" / „12 szans" — licznik szans w UI. */
export const szanseLabel = (n: number): string => `${n} ${plCount(n, 'szansa', 'szanse', 'szans')}`

/** „1 mecz" / „3 mecze" / „16 meczów". */
export const meczeLabel = (n: number): string => `${n} ${plCount(n, 'mecz', 'mecze', 'meczów')}`

// Surname for compact chart labels — last whitespace-separated token of the full
// name, pomijając końcowe sufiksy pokoleniowe ("Robert Deziel Jr." → "Deziel",
// "Mileta Rajović" → "Rajović"). Falls back to the whole string.
// „junior" / „senior" pisane w pełni też są sufiksem — SkillCorner zapisuje tak
// np. „Robert Edward Deziel Junior", więc bez nich podpis na wykresie brzmiał
// „Junior" zamiast „Deziel".
const NAME_SUFFIXES = new Set([
  'jr', 'jr.', 'junior', 'sr', 'sr.', 'senior', 'ii', 'iii', 'iv', 'v',
])
export function surname(fullName: string | null | undefined): string {
  if (!fullName) return ''
  const parts = fullName.trim().split(/\s+/)
  while (parts.length > 1 && NAME_SUFFIXES.has(parts[parts.length - 1].toLowerCase())) parts.pop()
  return parts[parts.length - 1] || fullName
}

// Etykiety-nazwiska z rozróżnianiem kolizji: gdy to samo nazwisko powtarza się w
// zbiorze, dokleja inicjał imienia („Urbański W."). Porównanie BEZ diakrytyków i
// wielkości liter (NFD + odsianie znaków spoza ASCII), bo ta sama osoba bywa w
// danych raz z „ń", raz bez. Zwraca funkcję pełne_imię → etykieta (wg zbioru).
export function surnameLabeler(
  fullNames: Iterable<string | null | undefined>,
): (full: string | null | undefined) => string {
  const norm = (s: string) => s.normalize('NFD').split('').filter((ch) => ch.charCodeAt(0) <= 127).join('').toLowerCase()
  const counts = new Map<string, number>()
  for (const n of fullNames) { const s = surname(n); if (s) counts.set(norm(s), (counts.get(norm(s)) ?? 0) + 1) }
  return (full) => {
    const s = surname(full)
    if (!s || (counts.get(norm(s)) ?? 0) <= 1) return s
    const first = (full ?? '').trim().split(/\s+/)[0] ?? ''
    return first ? `${s} ${first[0].toUpperCase()}.` : s
  }
}
